import { Briefcase } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  useApprove,
  useFacets,
  useQuestions,
  useReject,
  useRequeue,
  useSplitProposals,
  useTaxonomy,
  type QuestionScope,
  type QuestionSummary,
} from "@/api/queries";
import { useCan } from "@/auth/member";
import { BulkActionBar } from "@/components/bulk-action-bar";
import { PipelineNudge } from "@/components/pipeline-nudge";
import { QuestionDetail } from "@/components/question-detail";
import { SplitReview } from "@/components/split-review";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Combobox, FieldLabel, Input } from "@/components/ui/input";
import { Empty, ErrorNote, Panel, PaneHeader } from "@/components/ui/panel";
import { cn, formatScore } from "@/lib/utils";

/**
 * The review queue — DESIGN §7.5, ARCHITECTURE §10.
 *
 * §10 calls this the weakest view in Streamlit and the one people live in. Two things
 * it fixes, and they are the whole reason this screen exists:
 *
 *   Row click opens the record. Streamlit's `st.number_input("open question #")` made
 *   a reviewer read an id off a table and type it into a box.
 *
 *   j/k and arrow keys move through the queue, and a/r act on it. Reviewing 55
 *   pending questions is 55 round trips through a mouse otherwise.
 */
const KEYS = [
  { keys: ["j", "k"], label: "move" },
  { keys: ["x"], label: "select" },
  { keys: ["a"], label: "approve" },
  { keys: ["b"], label: "approve to the bank" },
  { keys: ["r"], label: "reject" },
  { keys: ["e"], label: "requeue" },
];

/** The keyboard contract is the feature; printing it as prose buried it. */
function Key({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="border-bank-border bg-bank-surface-2 inline-block min-w-[17px] rounded border border-b-2 px-1 py-0.5 text-center font-mono text-[10.5px] font-medium">
      {children}
    </kbd>
  );
}

function ReviewQueue() {
  // A Viewer reads the queue but decides nothing: no buttons, no note, no a/r/e/x keys, and no
  // checkboxes, which exist only to feed the bulk actions.
  const canReview = useCan("review");
  const [selected, setSelected] = useState<number | null>(null);
  const [note, setNote] = useState("");
  // The row that was just sent back into the pipeline, and where — the queue moves on
  // (it's no longer pending_review/stale) so this lives at page level, not the pane.
  const [nudge, setNudge] = useState<{ id: number; stage: string } | null>(null);

  // Checkbox selection for bulk actions — independent of `selected` (the row open in
  // the detail pane): a reviewer marks several rows without losing what they're
  // currently reading.
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [bulkNote, setBulkNote] = useState("");
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);

  const [stack, setStack] = useState("");
  const [bucket, setBucket] = useState("");
  // DESIGN §20.7's Source filter: questions written for a job description, or the rest.
  const [source, setSource] = useState<"" | "jd" | "bank">("");
  // "Approve to" as chosen for one question; any other question follows its own scope.
  const [approveTo, setApproveTo] = useState<{ id: number; scope: QuestionScope } | null>(null);
  const { data: facets } = useFacets();
  const { data: stackEntries } = useTaxonomy("stack");

  // A bucket is a property of a stack (§17.2), not of a question, so it has no server filter:
  // the queue is capped at 200 rows, and narrowing by the bucket's stack set in the browser is
  // cheaper than a join nobody else needs.
  const stackBuckets = useMemo(
    () => new Map((stackEntries ?? []).map((e) => [e.path, e.bucket ?? ""])),
    [stackEntries],
  );
  const buckets = useMemo(
    () => [...new Set([...stackBuckets.values()].filter((b) => b !== ""))].sort(),
    [stackBuckets],
  );
  const stackOptions = useMemo(
    () => (facets?.stack_paths ?? []).filter((s) => bucket === "" || stackBuckets.get(s) === bucket),
    [facets, bucket, stackBuckets],
  );

  const queue = useQuestions({
    status: ["pending_review", "stale"],
    limit: 200,
    ...(stack ? { stack_path: stack } : {}),
    ...(source ? { from_jd: source === "jd" } : {}),
  });
  // Stable identity: the effect below depends on this, and `?? []` is a new array
  // every render, which would re-run it on every keystroke elsewhere on the page.
  const items = useMemo(() => {
    const all = queue.data?.items ?? [];
    return bucket === "" ? all : all.filter((q) => q.stack_path && stackBuckets.get(q.stack_path) === bucket);
  }, [queue.data, bucket, stackBuckets]);

  const approve = useApprove();
  const reject = useReject();
  const requeue = useRequeue();
  const busy = approve.isPending || reject.isPending || requeue.isPending || bulkRunning;
  const actionError = approve.error ?? reject.error ?? requeue.error;

  /** Requeue (auto-detected stage, or `stage` forced — "extracted" re-enriches). */
  function doRequeue(id: number, stage?: string) {
    requeue.mutate({ id, note, stage }, { onSuccess: (result) => setNudge({ id, stage: result.status }) });
    setNote("");
  }

  const open = items.find((q) => q.id === selected) ?? null;
  const fromJD = (open?.jds ?? []).length > 0;
  const target: QuestionScope = (approveTo?.id === selected ? approveTo.scope : open?.scope) ?? "bank";

  /** Approve the open question — to `scope` when given, else to the Approve-to choice (§20.7). */
  function doApprove(id: number, scope?: QuestionScope) {
    act(() => approve.mutate({ id, note, ...(fromJD ? { scope: scope ?? target } : {}) }));
  }

  // Select the first row once the queue arrives, and follow the queue when the
  // selected row leaves it — approving the last item should not leave a dead pane.
  useEffect(() => {
    if (items.length === 0) {
      setSelected(null);
    } else if (selected === null || !items.some((q) => q.id === selected)) {
      setSelected(items[0]?.id ?? null);
    }
  }, [items, selected]);

  // A row that left the queue (acted on, elsewhere, or by this page's own bulk
  // action) can't stay checked — otherwise a stale id lingers in the count forever.
  useEffect(() => {
    setChecked((current) => {
      const next = new Set([...current].filter((id) => items.some((q) => q.id === id)));
      return next.size === current.size ? current : next;
    });
  }, [items]);

  const allChecked = items.length > 0 && items.every((q) => checked.has(q.id));
  const someChecked = items.some((q) => checked.has(q.id));
  const selectAllRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = someChecked && !allChecked;
  }, [someChecked, allChecked]);

  function toggleOne(id: number) {
    setChecked((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    setChecked(allChecked ? new Set() : new Set(items.map((q) => q.id)));
  }

  /** Fan `action` out to every checked id. Failures stay checked so a retry is a
   *  re-click, not a re-selection; successes vanish from `items` on the invalidated
   *  refetch (they're no longer pending_review/stale) and get pruned above.
   *
   *  Each outcome carries its own id rather than being correlated back to one by
   *  array index — `Promise.allSettled(ids.map(action))` and `ids` are same-length
   *  by construction, but nothing in the type system says so. */
  async function bulkAct(action: (id: number) => Promise<unknown>) {
    const ids = [...checked];
    if (ids.length === 0) return;
    setBulkRunning(true);
    setBulkError(null);
    const outcomes = await Promise.all(
      ids.map((id) =>
        action(id).then(
          () => ({ id, message: null as string | null }),
          (reason: unknown) => ({ id, message: reason instanceof Error ? reason.message : String(reason) }),
        ),
      ),
    );
    const failed = outcomes.filter((o): o is { id: number; message: string } => o.message !== null);
    if (failed.length > 0) {
      setBulkError(
        `${failed.length} of ${ids.length} failed: ${failed
          .slice(0, 3)
          .map((f) => f.message)
          .join("; ")}`,
      );
    }
    setChecked(new Set(failed.map((f) => f.id)));
    setBulkNote("");
    setBulkRunning(false);
  }

  function doBulkApprove() {
    void bulkAct((id) => approve.mutateAsync({ id, note: bulkNote }));
  }

  function doBulkReject() {
    void bulkAct((id) => reject.mutateAsync({ id, note: bulkNote }));
  }

  /** Requeue every checked row (auto-detected stage, or `stage` forced). */
  function doBulkRequeue(stage?: string) {
    void bulkAct((id) => requeue.mutateAsync({ id, note: bulkNote, stage }));
  }

  function move(delta: number) {
    if (items.length === 0) return;
    const index = items.findIndex((q) => q.id === selected);
    const next = Math.min(Math.max((index === -1 ? 0 : index) + delta, 0), items.length - 1);
    setSelected(items[next]?.id ?? null);
  }

  /** Act, then clear the note — it described the decision just made, not the next one. */
  function act(run: () => void) {
    run();
    setNote("");
  }

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      // Never steal a keystroke from someone typing a note.
      const target = event.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      switch (event.key) {
        case "j":
        case "ArrowDown":
          event.preventDefault();
          move(1);
          break;
        case "k":
        case "ArrowUp":
          event.preventDefault();
          move(-1);
          break;
        case "x":
          if (selected !== null && canReview) toggleOne(selected);
          break;
        case "a":
          if (selected !== null && canReview && !busy) doApprove(selected);
          break;
        case "b":
          if (selected !== null && canReview && !busy) doApprove(selected, "bank");
          break;
        case "r":
          if (selected !== null && canReview && !busy) act(() => reject.mutate({ id: selected, note }));
          break;
        case "e":
          if (selected !== null && canReview && !busy) doRequeue(selected);
          break;
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  return (
    <div className="flex h-full flex-col gap-3 p-4">
      <header className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <h1 className="text-base font-semibold tracking-tight">Review queue</h1>
        <span className="text-bank-muted font-mono text-xs tabular-nums">
          {queue.isLoading ? "loading…" : `${items.length} waiting`}
        </span>
        <div className="text-bank-muted ml-auto flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
          {(canReview ? KEYS : KEYS.slice(0, 1)).map(({ keys, label }) => (
            <span key={label} className="flex items-center gap-1">
              {keys.map((k) => (
                <Key key={k}>{k}</Key>
              ))}
              {label}
            </span>
          ))}
        </div>
      </header>

      <div className="grid max-w-3xl gap-3 sm:grid-cols-3">
        <div>
          <FieldLabel htmlFor="review-bucket">Bucket</FieldLabel>
          <Combobox
            id="review-bucket"
            value={bucket}
            onChange={(v) => {
              setBucket(v);
              // A stack outside the new bucket would silently empty the queue.
              if (stack && v !== "" && stackBuckets.get(stack) !== v) setStack("");
            }}
            options={[{ value: "", label: "any" }, ...buckets.map((b) => ({ value: b, label: b }))]}
          />
        </div>
        <div>
          <FieldLabel htmlFor="review-stack">Stack</FieldLabel>
          <Combobox
            id="review-stack"
            value={stack}
            onChange={setStack}
            options={[{ value: "", label: "any" }, ...stackOptions.map((s) => ({ value: s, label: s }))]}
          />
        </div>
        <div>
          <FieldLabel htmlFor="review-source">Source</FieldLabel>
          <Combobox
            id="review-source"
            value={source}
            onChange={(v) => setSource(v as typeof source)}
            options={[
              { value: "", label: "any" },
              { value: "jd", label: "from a job description" },
              { value: "bank", label: "not from a job description" },
            ]}
          />
        </div>
      </div>

      <ErrorNote error={queue.error ?? actionError} />
      {nudge && <PipelineNudge stage={nudge.stage} onDismiss={() => setNudge(null)} />}

      <div className="grid min-h-0 flex-1 grid-cols-[minmax(320px,2fr)_3fr] gap-3">
        <Panel className="flex min-h-0 flex-col overflow-hidden">
          <PaneHeader
            title={
              canReview ? (
                <label className="flex items-center gap-2">
                  <input
                    ref={selectAllRef}
                    type="checkbox"
                    checked={allChecked}
                    onChange={toggleAll}
                    disabled={items.length === 0}
                    aria-label="Select all waiting questions"
                    className="accent-bank-accent size-3.5"
                  />
                  Waiting
                </label>
              ) : (
                "Waiting"
              )
            }
          />
          {checked.size > 0 && (
            <BulkActionBar
              count={checked.size}
              busy={busy}
              note={bulkNote}
              onNoteChange={setBulkNote}
              onApprove={doBulkApprove}
              onReject={doBulkReject}
              onRequeue={() => doBulkRequeue()}
              onReenrich={() => doBulkRequeue("extracted")}
              onClear={() => setChecked(new Set())}
              error={bulkError}
            />
          )}
          <div className="min-h-0 flex-1 overflow-auto">
            {items.length === 0 && !queue.isLoading ? (
              <Empty>Nothing waiting. Ingest a document or run the pipeline.</Empty>
            ) : (
              <ul>
                {items.map((q) => (
                  <QueueRow
                    key={q.id}
                    question={q}
                    active={q.id === selected}
                    checked={checked.has(q.id)}
                    selectable={canReview}
                    onSelect={() => setSelected(q.id)}
                    onToggleCheck={() => toggleOne(q.id)}
                  />
                ))}
              </ul>
            )}
          </div>
        </Panel>

        <Panel className="flex min-h-0 flex-col overflow-hidden">
          <PaneHeader
            title={selected === null ? "No question selected" : `Question #${selected}`}
            actions={
              selected !== null &&
              canReview && (
                <>
                  <Button variant="primary" size="sm" disabled={busy} onClick={() => doApprove(selected)}>
                    Approve{fromJD ? (target === "bank" ? " to the bank" : " to this JD") : ""}
                  </Button>
                  <Button
                    size="sm"
                    disabled={busy}
                    onClick={() => act(() => reject.mutate({ id: selected, note }))}
                  >
                    Reject
                  </Button>
                  <Button size="sm" disabled={busy} onClick={() => doRequeue(selected)}>
                    Requeue
                  </Button>
                  <Button
                    size="sm"
                    disabled={busy}
                    title="Force a fresh enrichment pass, even though this row already has one"
                    onClick={() => doRequeue(selected, "extracted")}
                  >
                    Re-enrich
                  </Button>
                </>
              )
            }
          />
          <div className="min-h-0 flex-1 overflow-auto">
            {selected === null ? (
              <Empty>Select a question.</Empty>
            ) : (
              <QuestionDetail id={selected} onOpenQuestion={setSelected} />
            )}
          </div>
          {selected !== null && canReview && (
            <div className="border-bank-border bg-bank-surface-2 space-y-2 border-t px-4 py-3">
              {fromJD && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="text-bank-muted font-mono text-[10.5px] tracking-[0.07em] uppercase">
                    Approve to
                  </span>
                  <div
                    role="radiogroup"
                    aria-label="Where the approved question goes"
                    className="border-bank-border-strong bg-bank-surface inline-flex gap-0.5 rounded-md border p-0.5"
                  >
                    {(
                      [
                        ["jd_only", "This JD only"],
                        ["bank", "The bank"],
                      ] as const
                    ).map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        role="radio"
                        aria-checked={target === value}
                        onClick={() => selected !== null && setApproveTo({ id: selected, scope: value })}
                        className={cn(
                          "focus-visible:ring-bank-accent rounded px-2.5 py-1 text-xs focus-visible:ring-2 focus-visible:outline-none",
                          target === value
                            ? "bg-bank-accent-wash text-bank-accent-ink font-medium"
                            : "text-bank-muted hover:text-bank-ink",
                        )}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <span className="text-bank-muted text-xs">
                    “This JD only” keeps it to members practising that JD; it can be promoted from the JD's
                    Questions tab later.
                  </span>
                </div>
              )}
              <FieldLabel htmlFor="review-note">
                Note — recorded on the status_transitions row (§7.5), whatever you choose
              </FieldLabel>
              <Input
                id="review-note"
                value={note}
                placeholder="why, for whoever reads this in six months"
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}

function QueueRow({
  question,
  active,
  checked,
  selectable,
  onSelect,
  onToggleCheck,
}: {
  question: QuestionSummary;
  active: boolean;
  checked: boolean;
  selectable: boolean;
  onSelect: () => void;
  onToggleCheck: () => void;
}) {
  return (
    <li
      className={cn(
        "border-bank-border relative flex items-start gap-2.5 border-b px-3.5 py-2.5 transition-colors",
        active ? "bg-bank-accent-wash" : "hover:bg-bank-surface-2",
      )}
    >
      {/* The stripe, not a fill, is what marks the selected row: in a neutral
          palette a grey fill reads as "hovered" as readily as "here". */}
      {active && <span className="bg-bank-accent absolute inset-y-0 left-0 w-0.5" aria-hidden="true" />}
      {/* A sibling of the button below, not nested in it — a checkbox inside a
          clickable button fires both handlers on one click. */}
      {selectable && (
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggleCheck}
          aria-label={`Select question #${question.id}`}
          className="accent-bank-accent mt-0.5 size-3.5 shrink-0"
        />
      )}
      <button
        onClick={onSelect}
        aria-current={active ? "true" : undefined}
        className="min-w-0 flex-1 text-left"
      >
        <div className="mb-1.5 flex items-center gap-2">
          <span className="text-bank-muted font-mono text-[11px] tabular-nums">#{question.id}</span>
          <Badge value={question.status} />
          <span className="ml-auto font-mono text-[11px] font-medium tabular-nums">
            q {formatScore(question.quality_score)}
          </span>
        </div>
        <p className="question-text line-clamp-2 text-sm">{question.question_text}</p>
        {(question.jds ?? []).length > 0 && (
          <p className="mt-1 flex flex-wrap gap-1.5">
            {(question.jds ?? []).map((jd) => (
              <span
                key={jd.jd_id}
                className="bg-bank-accent-wash text-bank-accent-ink inline-flex h-5 items-center gap-1 rounded px-1.5 text-[11px]"
              >
                <Briefcase className="size-3" aria-hidden="true" />
                {jd.title}
              </span>
            ))}
          </p>
        )}
        <p className="text-bank-muted mt-1 truncate font-mono text-[11px]">
          {question.stack_path ?? "—"} · {question.primary_concept ?? "—"}
          {question.cluster_size && question.cluster_size > 1 ? ` · cluster of ${question.cluster_size}` : ""}
        </p>
        {/* §17.11.6: a split pair reaches review together, and each end says so. */}
        {question.linked_follow_up && (
          <p className="text-bank-accent-ink mt-1 line-clamp-1 font-mono text-[11px]">
            ↳ follow-up #{question.linked_follow_up.id} · {question.linked_follow_up.question_text}
          </p>
        )}
        {(question.follow_up_of ?? []).length > 0 && (
          <p className="text-bank-accent-ink mt-1 font-mono text-[11px]">
            ↩ follow-up of {(question.follow_up_of ?? []).map((id) => `#${id}`).join(", ")}
          </p>
        )}
      </button>
    </li>
  );
}

/**
 * The Review screen is two queues (DESIGN §17.11.6): the questions waiting for a decision, and the
 * proposals to split long ones. They are separate components so the queue's j/k/a/r/e shortcuts are
 * only listening while it is on screen — a stray `a` on the Splits tab must not approve a question.
 */
export default function Review() {
  // `?tab=splits` is how the Ingest page's Jobs table links to a split run's proposals.
  const [params] = useSearchParams();
  const [tab, setTab] = useState<"queue" | "splits">(params.get("tab") === "splits" ? "splits" : "queue");
  const proposals = useSplitProposals("proposed", 0, 1);
  const waiting = proposals.data?.counts.proposed ?? 0;
  return (
    <div className="flex h-full flex-col">
      <div
        role="tablist"
        aria-label="Review queues"
        className="border-bank-border flex gap-1 border-b px-4 pt-2"
      >
        {(
          [
            ["queue", "Queue"],
            ["splits", "Splits"],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            role="tab"
            aria-selected={tab === value}
            onClick={() => setTab(value)}
            className={cn(
              "focus-visible:ring-bank-accent -mb-px rounded-t-md border-b-2 px-3 py-1.5 text-sm font-medium focus-visible:ring-2 focus-visible:outline-none",
              tab === value
                ? "border-bank-accent text-bank-ink"
                : "text-bank-muted hover:text-bank-ink border-transparent",
            )}
          >
            {label}
            {value === "splits" && waiting > 0 && (
              <span className="bg-bank-attention-wash text-bank-attention-ink ml-1.5 rounded px-1.5 py-0.5 font-mono text-[10.5px] tabular-nums">
                {waiting}
              </span>
            )}
          </button>
        ))}
      </div>
      <div className="flex min-h-0 flex-1 flex-col">
        {tab === "queue" ? <ReviewQueue /> : <SplitReview onOpenQueue={() => setTab("queue")} />}
      </div>
    </div>
  );
}
